import React, { useCallback, useMemo, useState } from "react";
import { useFranchiseList } from "@dealer-center/ddc-pages-franchise-management-fetch";
import type { IFranchiseTableData } from "@dealer-center/ddc-pages-franchise-management-interface";
import type { TypeSafeTableColumns } from "@dealer-center/core-components";
import {
  Alert,
  Box,
  Card,
  CardContent,
  IconButton,
  SearchInput,
  Table,
  TrashIcon,
  Typography
} from "@dealer-center/core-components";
import { getFilteredData } from "./helper";
import { useFranchiseModals } from "./modals";
import styled from "styled-components";

const StyledAbbreviationText = styled.span`
  color: var(--ids-color-primary-dark, #005a9e);
  font-weight: 500;
`;

export const FranchiseList: React.FC = () => {
  const { data, error, isLoading, isFetching } = useFranchiseList();
  const [filterValue, setFilterValue] = useState<string>("");
  const { showDeleteModal, modal } = useFranchiseModals();

  const handleOnDelete = useCallback(
    (id: string, name: string) => {
      showDeleteModal({ id, name });
    },
    [showDeleteModal]
  );

  const columns = useMemo<TypeSafeTableColumns<IFranchiseTableData>>(
    () => [
      {
        dataIndex: "name",
        title: "Name"
      },
      {
        dataIndex: "abbreviation",
        title: "Abbreviation",
        render: (abbreviation: string): React.ReactNode => (
          <StyledAbbreviationText>{abbreviation}</StyledAbbreviationText>
        )
      },
      {
        dataIndex: "formattedCreated",
        title: "Created",
        defaultSortOrder: "descend",
        render: (formattedCreated: IFranchiseTableData["created"]): string =>
          formattedCreated,
        sorter: (a: IFranchiseTableData, b: IFranchiseTableData): number =>
          new Date(a.created).getTime() - new Date(b.created).getTime()
      },
      {
        dataIndex: "formattedLastModified",
        title: "Last Updated",
        render: (
          formattedLastModified: IFranchiseTableData["formattedLastModified"]
        ): string => formattedLastModified,
        sorter: (a: IFranchiseTableData, b: IFranchiseTableData): number =>
          new Date(a.lastModified).getTime() -
          new Date(b.lastModified).getTime()
      },
      {
        columnSortable: false,
        title: "Actions",
        dataIndex: "id",
        render: (
          id: IFranchiseTableData["id"],
          record: IFranchiseTableData
        ): React.ReactNode => {
          return (
            <IconButton
              buttonStyle="secondary"
              data-testid="franchise-list-delete-button"
              // TODO: Enable delete functionality when ready
              disabled={true}
              onClick={() => handleOnDelete(id, record.name)}
            >
              <TrashIcon title="Delete" />
            </IconButton>
          );
        }
      }
    ],
    []
  );

  const filteredData = useMemo<IFranchiseTableData[]>(() => {
    if (!data) return [];
    const filteredColumns = columns.filter(c => c.dataIndex !== "id");
    const filtered = getFilteredData(data, filterValue, filteredColumns);
    return filtered;
  }, [data, filterValue]);

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setFilterValue(event.target.value ?? "");
    },
    []
  );

  const franchiseCardContent: React.ReactNode = (
    <CardContent>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 2,
          alignItems: "center"
        }}
      >
        <Typography sx={{ fontWeight: "bold", marginBottom: 2 }} variant="h4">
          Franchise List{" "}
          {isLoading || isFetching ? "" : `(${data?.length ?? 0})`}
        </Typography>
        <SearchInput
          data-testid="franchise-list-search-input"
          inputValue={filterValue}
          placeholder="Search franchises..."
          sx={{ maxWidth: "300px" }}
          onInputChange={handleSearchChange}
        />
      </Box>
      <Table.Table
        sortableColumns
        borderType="contained"
        columns={columns}
        currentPageSize={10}
        data={filteredData || []}
        displayFilter={false}
        emptyText="No data"
        isLoading={isLoading || isFetching}
      />
    </CardContent>
  );

  return (
    <>
      {error && (
        <Alert role="alert" sx={{ marginBottom: "16px" }} type="error">
          {error.message}
        </Alert>
      )}
      {!error && <Card content={franchiseCardContent} />}
      {modal}
    </>
  );
};
